import React, { useState } from 'react';
import { Play, X, ExternalLink } from 'lucide-react';
import { useSite } from '../context/SiteContext';

const YouTubeModal = ({ videoId, onClose, title }) => (
  <div
    className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
    onClick={onClose}
  >
    <div
      className="relative w-full max-w-5xl aspect-video rounded-xl overflow-hidden border border-white/10 bg-black"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute -top-3 -right-3 z-10 h-9 w-9 rounded-full bg-white text-black flex items-center justify-center hover:bg-brand-green-light btn-transition"
      >
        <X className="h-4 w-4" />
      </button>
      <iframe
        title={title}
        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
        className="w-full h-full"
      />
    </div>
  </div>
);

const FeaturedBrands = () => {
  const { featuredBrands = [] } = useSite();
  const [active, setActive] = useState(null);

  return (
    <section id="brands" className="section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-brand-green-light">Featured partners</p>
            <h2 className="mt-3 text-3xl md:text-4xl font-bold text-white leading-tight">
              Best-in-class brands. One partner.
            </h2>
          </div>
          <p className="max-w-md text-slate-400">
            Watch what our brand partners are innovating — and how we bring them to your
            business.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {featuredBrands.map((b) => (
            <div
              key={b.name}
              className="relative rounded-2xl overflow-hidden border border-white/8 bg-white/[0.02] card-lift"
            >
              <div className="relative aspect-video overflow-hidden">
                <img
                  src={b.images[0]}
                  alt={b.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
                <div
                  aria-hidden
                  className="absolute inset-0"
                  style={{
                    background:
                      'linear-gradient(180deg, rgba(6,8,13,0.15) 0%, rgba(6,8,13,0.75) 100%)',
                  }}
                />
                {b.videoId && (
                  <button
                    onClick={() => setActive(b)}
                    className="absolute inset-0 flex items-center justify-center group"
                    aria-label={`Play ${b.videoTitle}`}
                  >
                    <span className="h-16 w-16 rounded-full bg-brand-green text-black flex items-center justify-center shadow-lg shadow-brand-green/40 group-hover:scale-110 btn-transition">
                      <Play className="h-6 w-6 fill-current" />
                    </span>
                  </button>
                )}
                <div className="absolute top-4 left-4 rounded-md bg-black/60 border border-white/10 px-2.5 py-1 backdrop-blur-md">
                  <img src={b.logo} alt={b.name} className="h-6 w-auto" style={{ filter: 'brightness(1.4)' }} />
                </div>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-white text-lg font-semibold">{b.name}</h3>
                    <p className="mt-1 text-sm text-slate-400">{b.tagline}</p>
                  </div>
                  {b.website && (
                    <a
                      href={b.website}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-brand-green-light hover:text-white flex items-center gap-1 whitespace-nowrap btn-transition"
                    >
                      Visit <ExternalLink className="h-3 w-3" />
                    </a>
                  )}
                </div>
                {b.features && (
                  <ul className="mt-4 grid grid-cols-2 gap-2">
                    {b.features.map((f) => (
                      <li key={f} className="text-xs text-slate-300 flex items-start gap-2">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-brand-pink flex-shrink-0" />
                        {f}
                      </li>
                    ))}
                  </ul>
                )}
                {b.videoId && (
                  <button
                    onClick={() => setActive(b)}
                    className="mt-4 text-xs text-brand-green-light hover:text-white btn-transition"
                  >
                    ▶  Play {b.videoTitle}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {active && active.videoId && (
        <YouTubeModal
          videoId={active.videoId}
          title={active.videoTitle}
          onClose={() => setActive(null)}
        />
      )}
    </section>
  );
};

export default FeaturedBrands;
